import { call, put, select } from 'typed-redux-saga'

import { showError } from '@store/ui/error/actions'
import { setLastServiceResponse } from '@store/session/actions'
import { ApplicationState } from '@store/types'
import { StepRefin } from '@utils/enums'
import postGenerateIdUnicoLink from '@services/postGenerateIdUnicoLink'
import { BridgeService } from '@services/bridge'
import routes from '@routes/routes'
import { encryptString } from '@utils/encrypt'
import { decryptString } from '@utils/decrypt'

import { generateIdUnicoLinkRequest, setLoadingGenerateIdUnicoLink } from '../actions'
import type { GenerateIdUnicoLinkRequestPayload } from '../types'

interface Props {
  payload: GenerateIdUnicoLinkRequestPayload
  type: string
}

export default function* generateIdUnicoLinkSaga({ payload }: Props) {
  const { numeroProposta } = yield* select((s: ApplicationState) => s.refinanciamento.proposta)
  const { accessToken } = yield* select((s: ApplicationState) => s.session)
  const { seed } = yield* select((s: ApplicationState) => s.biometria)

  yield* put(setLoadingGenerateIdUnicoLink(true))
  try {
    const siteUrl = process.env.REACT_APP_REFINANCIAMENTO ?? ''
    if (!siteUrl) {
      throw new Error('REACT_APP_REFINANCIAMENTO is missing')
    }

    const origin = siteUrl.endsWith(routes.REFIN_v2)
      ? siteUrl.slice(0, -routes.REFIN_v2.length)
      : siteUrl
    const callbackUrl = `${origin}${routes.REVISAO}`

    const requestBody: {
      origin: 'REFINANCING_WEB' | 'REFINANCING_APP'
      clientId: string
      callbackUrl: string
    } = {
      origin: BridgeService.isBrowser() ? 'REFINANCING_WEB' : 'REFINANCING_APP',
      clientId: String(numeroProposta),
      callbackUrl,
    }

    const encryptedRequestBody = yield* call(encryptString, seed, JSON.stringify(requestBody))

    const { data } = yield* call(
      postGenerateIdUnicoLink,
      { encryptData: encryptedRequestBody, seed },
      accessToken,
    )

    const response = yield* call(decryptString, data.seed, data.encryptData)

    const parsedResponse = JSON.parse(response)

    const { biometricUrl } = parsedResponse

    yield put(setLastServiceResponse({ code: 'OK', serviceName: 'postGenerateIdUnicoLink' }))
    yield payload.callback(biometricUrl)
  } catch (_error) {
    yield put(setLastServiceResponse({ code: 'ERROR', serviceName: 'postGenerateIdUnicoLink' }))
    yield put(
      showError({
        title: 'Ocorreu um erro ao gerar link de biometria',
        message: 'Por favor tente novamente.',
        actionTitle: 'Tentar Novamente',
        code: StepRefin.ERRO_GERAR_LINK_BIOMETRIA,
        actionCallback: () => generateIdUnicoLinkRequest(payload),
        block: true,
      }),
    )
  } finally {
    yield* put(setLoadingGenerateIdUnicoLink(false))
  }
}
